import { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Constants
const NODE_RADIUS = 40;
const ROOT_TO_FIRST_LEVEL_SPACING = 200; // Custom spacing from root to level 1
const FIRST_LEVEL_TO_OTHER_SPACING = 150;

const LOSTreeView = ({ data, config = {
  ROOT_NODE_COLOR: '#9e703c',
  PARENT_NODE_COLOR: '#367378',
  CHILD_NODE_COLOR: '#b7503f',
  LINK_COLOR: "#91b6bb",
  TEXT_COLOR: "#ffffff",
  FONT_SIZE: '12px',
} }) => {
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const [error, setError] = useState(null); // State for error handling

  const {t} = useTranslation();

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Validate data structure
  useEffect(() => {
    const isValid = validateDataStructure(data);
    if (!isValid) {
      setError(t("Invalid data structure"));
    } else {
      setError(null);
    }
  }, [data]);

  if (error) {
    return (
      <div style={{ color: 'red', textAlign: 'center', marginTop: '20px' }}>
        <h3>{t("Error")}</h3>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <ZoomableSVG width={dimensions.width} height={dimensions.height}>
      <TreeViewComponent width={dimensions.width} height={dimensions.height} data={data} config={config}/>
    </ZoomableSVG>
  );
};

// Helper function to validate the data structure
function validateDataStructure(node) {
  if (!node || typeof node !== 'object') return false;
  if (!node.id || !node.name) return false;
  if (!node.connections || (node.connections && !Array.isArray(node.connections))) return false;
  if (node.connections) {
    return node.connections.every(validateDataStructure);
  }
  return true;
}

// Helper Functions
function transformData(node) {
  if (!node) return null;
  return {
    id: node.id,
    name: node.name,
    children: (node.connections || []).map(transformData),
  };
}

function getCenterChild(children) {
  if (!children || children.length === 0) return null;
  return children[Math.floor((children.length - 1) / 2)];
}

// Initialization Function
function initialize(svg, width, height, data, config) {
  const radius = Math.min(width, height) / 2 - 60;

  const levelArcGenerator = d3.arc()
    .innerRadius(d => d.radius)
    .outerRadius(d => d.radius)
    .startAngle(d => d.startAngle)
    .endAngle(d => d.endAngle);

  const tree = d3.tree()
    .size([2 * Math.PI, radius])
    .separation((a, b) => {
      return (a.parent === b.parent ? 0.3 : 0.5) / a.depth;
    });

  const transformedData = transformData(data);
  const root = tree(
    d3.hierarchy(transformedData)
      .sort((a, b) => d3.ascending(a.data.name, b.data.name))
  );

  // Calculate equal spacing for levels > 1
  const maxDepth = d3.max(root.descendants(), d => d.depth);
  const remainingRadius = ROOT_TO_FIRST_LEVEL_SPACING + FIRST_LEVEL_TO_OTHER_SPACING; // Remaining radius for levels > 1
  const equalLevelSpacing = remainingRadius / (maxDepth - 1); // Equal spacing for levels > 1

  // Apply spacing to all nodes and Custom radius for nodes based on some property
  root.descendants().forEach(d => {
    d.r = NODE_RADIUS;
    if (d.depth === 1) {
      d.y = ROOT_TO_FIRST_LEVEL_SPACING; // Custom spacing for level 1
    } else if (d.depth > 1) {
      d.y = ROOT_TO_FIRST_LEVEL_SPACING + equalLevelSpacing * (d.depth - 1);
    }
  });

  // Links
  svg.append("g")
    .attr("fill", "none")
    .attr("stroke", "transparent")
    .attr("stroke-width", 2)
    .selectAll()
    .data(root.links().filter(link => link.source.depth !== 0))
    .join("path")
    .attr("d", d3.linkRadial()
      .angle(d => d.x)
      .radius(d => d.y));

  // Parent-Child Lines
  svg.append("g")
    .attr("fill", "none")
    .attr("stroke", config.LINK_COLOR)
    .attr("stroke-width", 2)
    .selectAll()
    .data(root.descendants().filter(d => d.children && d.depth !== 0))
    .join("line")
    .attr("x1", d => d.y * Math.cos(d.x - Math.PI / 2))
    .attr("y1", d => d.y * Math.sin(d.x - Math.PI / 2))
    .attr("x2", d => {
      const centerChild = getCenterChild(d.children);
      return centerChild ? centerChild.y * Math.cos(centerChild.x - Math.PI / 2) : d.y;
    })
    .attr("y2", d => {
      const centerChild = getCenterChild(d.children);
      return centerChild ? centerChild.y * Math.sin(centerChild.x - Math.PI / 2) : d.y;
    });

  // Level Arcs
  svg.append("g")
    .attr("fill", "none")
    .attr("stroke", config.LINK_COLOR)
    .attr("stroke-width", 2)
    .selectAll()
    .data(root.descendants().filter(d => d.children))
    .join("path")
    .attr("d", d => {
      const children = d.children;
      if (children.length < 2) return null;

      const levelRadius = children[0].y;
      const startAngle = children[0].x;
      const endAngle = children[children.length - 1].x;

      const arcData = {
        radius: levelRadius,
        startAngle,
        endAngle,
      };

      return levelArcGenerator(arcData);
    });

  // Nodes
  svg.append("g")
    .selectAll()
    .data(root.descendants())
    .join("circle")
    .attr("transform", d => `rotate(${d.x * 180 / Math.PI - 90}) translate(${d.y},0)`)
    .attr("fill", d => d.depth === 0 ? config.ROOT_NODE_COLOR : (d.children ? config.PARENT_NODE_COLOR : config.CHILD_NODE_COLOR))
    .attr("r", d => d.r);

  // Center Point
  svg.append("circle")
    .attr("cx", 0)
    .attr("cy", 0)
    .attr("r", 5)
    .attr("fill", "transparent");

  // Level Circles
  const uniqueLevels = [...new Set(root.descendants().map(d => d.y))];
  uniqueLevels.forEach(levelRadius => {
    svg.append("circle")
      .attr("cx", 0)
      .attr("cy", 0)
      .attr("r", levelRadius)
      .attr("fill", "none")
      .attr("stroke", "transparent")
      .attr("stroke-width", 2);
  });

  // Text
  svg.append("g")
    .selectAll()
    .data(root.descendants())
    .join("text")
    .attr("transform", d => `translate(${d.y * Math.cos(d.x - Math.PI / 2)}, ${d.y * Math.sin(d.x - Math.PI / 2)})`)
    .attr("text-anchor", "middle")
    .style("fill", config.TEXT_COLOR)
    .style("font-size", config.FONT_SIZE)
    .each(function (d) {
      const words = d.data.name.split(" ");
      const maxWidth = 10; // Maximum line width in characters
      const lineHeight = 1.2; // Line height in em units
      let line = '';
      let lineNumber = 0;
      const textElement = d3.select(this);

      const lines = []; // Store lines for centering calculation

      // Wrap text
      words.forEach(word => {
        const testLine = line + word + ' ';
        if (testLine.length > maxWidth) {
          lines.push(line.trim());
          line = word + ' ';
          lineNumber++;
        } else {
          line = testLine;
        }
      });
      lines.push(line.trim()); // Push the last line

      // Calculate center offset for text alignment
      const totalLines = lines.length;
      const offset = -(totalLines - 1) * lineHeight / 2.8; // Centering offset

      // Add tspans for each line
      lines.forEach((lineText, i) => {
        textElement.append("tspan")
          .attr("x", 0)
          .attr("y", offset + i * lineHeight + "em")
          .text(lineText);
      });
    });
}

// TreeViewComponent
const TreeViewComponent = ({ width, height, data, config }) => {
  const chartGroupRef = useRef();

  useEffect(() => {
    const svg = d3.select(chartGroupRef.current);
    svg.selectAll("*").remove(); // Clear previous visualization
    initialize(svg, width, height, data, config);
  }, [width, height, data]);

  return <g ref={chartGroupRef}></g>; // Only group element, no svg
};

const ZoomableSVG = ({ children, width, height }) => {
  const svgRef = useRef();

  useEffect(() => {
    const zoom = d3.zoom().on("zoom", (event) => {
      const { x, y, k } = event.transform;
      d3.select(svgRef.current)
        .select("g.zoom-layer")
        .attr("transform", `translate(${x},${y})scale(${k})`);
    });

    d3.select(svgRef.current).call(zoom);

    return () => d3.select(svgRef.current).on(".zoom", null); // Cleanup zoom
  }, []);

  return (
    <svg ref={svgRef} id="visualization-layer" width={width} height={height}>
      {/* Outer <g> for zoom transformations */}
      <g className="zoom-layer">
        {/* Inner <g> for centering */}
        <g transform={`translate(${width / 2}, ${height / 2})`}>{children}</g>
      </g>
    </svg>
  );
};

LOSTreeView.propTypes = {
  data: PropTypes.object.isRequired,
};

export default LOSTreeView;
